import type {
  ArchetypeId,
  Trait,
  LifeChoice,
  WorldDetail,
  Relationship,
  Dossier,
} from "../types";
import type { NarrativeContext, NarrativeResult } from "../narrative";

// 24 unique names so modulo indexing across seeds and archetypes yields diverse identities
const NAMES: string[] = [
  "Cassian Vance",
  "Lyra Oakhaven",
  "Thorne Mercer",
  "Ariadne Vane",
  "Corin Sunder",
  "Mira Hallowell",
  "Tarek Lin",
  "Zephyr Sterling",
  "Kaelen Voss",
  "Selah Marlowe",
  "Orion Drake",
  "Bryn Thorne",
  "Vespera Cruz",
  "Dorian Finch",
  "Naomi Valen",
  "Silas Sterling",
  "Kira Sol",
  "Alden Rhee",
  "Junia Cross",
  "Rhys Calder",
  "Elowen Ray",
  "Marek Holt",
  "Talia Winter",
  "Rowan Castor",
];

interface ArchetypeContent {
  occupations: string[];
  titles: string[];
  taglines: string[];
  origins: string[];
  turningPoints: string[];
  todays: string[];
  dilemmas: string[];
  nearFutures: string[];
  longArcs: string[];
  choiceTrios: Array<[LifeChoice, LifeChoice, LifeChoice]>;
  skills: string[][];
  motivations: string[][];
  relationships: Array<[Relationship, Relationship, Relationship]>;
  achievements: string[][];
  failures: string[][];
  worldDetails: Array<[WorldDetail, WorldDetail, WorldDetail, WorldDetail]>;
}

const CONTENT: Record<ArchetypeId, ArchetypeContent> = {
  divergent: {
    occupations: [
      "Subway Tunnel Geotechnical Surveyor",
      "Restoration Architectural Historian",
      "Coastal Hydrographic Cartographer",
      "Secondary School Physics Instructor",
    ],
    titles: [
      "Chronicler of Quiet Concrete",
      "Keeper of the Lost Blueprints",
      "Surveyor of Sunken Basements",
      "Curator of the Ordinary Fork",
    ],
    taglines: [
      "One missed morning commute redirected twenty years of quiet engineering.",
      "A preserved archive in a river valley holds the work that might have been.",
      "Charting the structural fractures of an alternate municipal coastline.",
      "Small domestic turning points echoed across three decades of civic restoration.",
    ],
    origins: [
      "Raised in a drafty railway flat where blueprints lined the kitchen counters, learning to measure load-bearing limits before ever seeing the coast.",
      "Grew up beside an inland freight depot, observing how routine industrial schedules shaped every neighbour's sense of possibility.",
      "Formed in a quiet harbour town where evening fog stalled navigation and taught patience with stubborn instruments.",
      "Spent early years in an archive reading room, copying hand-drawn municipal transit surveys under the hum of fluorescent tubes.",
    ],
    turningPoints: [
      "Refused a prestigious civil engineering post overseas to catalog deteriorating railway culverts across the home valley.",
      "Chose an unfunded heritage masonry apprenticeship over a secure corporate design desk during a major construction boom.",
      "Stayed behind during a regional evacuation to document the structural stability of centuries-old brick stormwater conduits.",
      "Declined an administrative partnership to establish an independent field office measuring ground subsidence along the estuary.",
    ],
    todays: [
      "Inspects century-old masonry foundations each dawn before the morning trains begin their rumble.",
      "Spends rainy afternoons digitizing waterlogged elevation charts in an unheated municipal basement.",
      "Measures ground-penetrating radar echoes along tidal embankments, noting shifts of mere millimetres.",
      "Conducts hands-on surveying seminars for curious local apprentices in a converted tram shed.",
    ],
    dilemmas: [
      "Preserving historic civil infrastructure delays modern flood defenses that vulnerable communities urgently require.",
      "Publishing evidence of deep soil fatigue could force the closure of an essential commuter rail branch line.",
      "Accepting corporate restoration sponsorship would fund years of field study but compromise public access to the archive.",
      "Staying dedicated to regional survey work has limited personal mobility while broader architectural movements advance elsewhere.",
    ],
    nearFutures: [
      "Preparing a comprehensive structural survey of the lower valley aqueduct before the winter rainfall peaks.",
      "Finalizing an open-access field guide to regional soil mechanics for self-taught municipal technicians.",
      "Refurbishing an abandoned masonry testing press to verify historical lime mortar endurance curves.",
      "Submitting an independent technical report on estuary embankment settlement to regional planners.",
    ],
    longArcs: [
      "Will leave behind an irreplaceable forty-year public baseline of structural soil shifts that protects future generations.",
      "Their quiet dedication will preserve the vernacular architecture of an entire river corridor from speculative demolition.",
      "An unassuming archive of handwritten field journals will become the foundational reference for low-carbon restoration.",
      "Generations of transit engineers will credit this life with proving that maintenance is as vital as monumental invention.",
    ],
    choiceTrios: [
      [
        {
          age: "Age 19",
          choice: "Enrolled in field surveying rather than structural management",
          roadNotTaken: "A deskbound corporate trajectory with guaranteed advancement",
          consequence: "Mastered practical instrument calibration in all weather conditions",
        },
        {
          age: "Age 28",
          choice: "Stayed in the home river basin during a regional hiring boom",
          roadNotTaken: "Relocating to an international design firm in the capital",
          consequence: "Developed unmatched familiarity with historical soil profiles",
        },
        {
          age: "Age 36",
          choice: "Published an unvarnished audit of deteriorating drainage systems",
          roadNotTaken: "Accepting a quiet consultancy that required non-disclosure",
          consequence: "Secured municipal maintenance budgets at the cost of private commissions",
        },
      ],
      [
        {
          age: "Age 21",
          choice: "Apprenticed with traditional lime-mortar artisans",
          roadNotTaken: "Pursuing modern precast concrete manufacturing",
          consequence: "Gained rare tactile insight into long-term masonry breathing",
        },
        {
          age: "Age 30",
          choice: "Refused to certify an unverified embankment proposal",
          roadNotTaken: "Signing off to expedite a major logistics warehouse project",
          consequence: "Endured three lean seasons before field integrity was acknowledged",
        },
        {
          age: "Age 41",
          choice: "Converted a personal workshop into an open community diagnostic lab",
          roadNotTaken: "Commercializing proprietary soil analysis instruments",
          consequence: "Fostered a network of civic technicians across the watershed",
        },
      ],
      [
        {
          age: "Age 18",
          choice: "Took an entry-level position charting tidal silt shifts",
          roadNotTaken: "Attending a conventional regional business college",
          consequence: "Learned the cadence of the estuary from working boat crews",
        },
        {
          age: "Age 27",
          choice: "Documented historic culverts slated for unrecorded burial",
          roadNotTaken: "Moving to a high-speed rail consortium in another district",
          consequence: "Saved irreplaceable flood overflow routing from permanent loss",
        },
        {
          age: "Age 39",
          choice: "Partnered with public schools to teach hands-on cartography",
          roadNotTaken: "Focusing solely on private industrial consultation",
          consequence: "Mentored a generation of environmentally grounded civic surveyors",
        },
      ],
      [
        {
          age: "Age 20",
          choice: "Chose field instrumentation over theoretical modelling",
          roadNotTaken: "Remaining in a university computational department",
          consequence: "Spent two decades outdoors understanding real-world measurement error",
        },
        {
          age: "Age 29",
          choice: "Exposed foundation settling under a major public auditorium",
          roadNotTaken: "Concealing the report to avoid political embarrassment",
          consequence: "Forced structural retrofitting that later prevented a catastrophic collapse",
        },
        {
          age: "Age 44",
          choice: "Authored an independent atlas of vulnerable urban basements",
          roadNotTaken: "Retiring early into quiet private drafting work",
          consequence: "Created an enduring public safety standard adopted across the province",
        },
      ],
    ],
    skills: [
      ["Subsurface acoustic sounding", "Historical lime mortar analysis", "Precision optical leveling", "Municipal archive archaeology"],
      ["Hydrological silt modelling", "Structural crack propagation tracking", "Geotechnical core logging", "Emergency shoring design"],
      ["Tidal sediment cartography", "Historical ledger reconstruction", "Low-frequency soil resonance sensing", "Community infrastructure advocacy"],
      ["Hand drafting of transit conduits", "Foundation load calculations", "Vernacular brick conservation", "Field team calibration"],
    ],
    motivations: [
      ["Preserving the physical memory of ordinary civic work against rapid commercial erasure.", "Ensuring daily commuter safety through meticulous, uncelebrated structural maintenance."],
      ["Demonstrating that durable materials and patient inspection outlast fleeting development trends.", "Protecting working-class neighborhoods from hidden geotechnical hazards."],
      ["Keeping public technical archives completely open and accessible to all residents.", "Proving that quiet local stewardship yields greater stability than distant speculative capital."],
      ["Documenting the subtle physical dialogue between human settlement and changing river systems.", "Equipping the next generation with reliable practical diagnostic skills."],
    ],
    relationships: [
      [
        { who: "Arthur Pendelton", role: "Retired Municipal Inspector", bond: "A gruff mentor who taught the value of checking mortar joints by hand at dawn." },
        { who: "Elena Rostova", role: "Transit Authority Civil Lead", bond: "A professional rival who questions conservative load margins yet respects the field data." },
        { who: "Marcus Vance", role: "Older Brother & Carpenter", bond: "A steadfast sibling who grounds late-night theoretical doubts in solid timber realities." },
      ],
      [
        { who: "Dr. Henry Briggs", role: "Geotechnical Geologist", bond: "An academic collaborator who exchanges core drill logs for historical cellar maps." },
        { who: "Clara Shen", role: "Borough Planning Officer", bond: "A skeptical administrator who slowly became an ally after a crucial culvert rescue." },
        { who: "Toby Miller", role: "Junior Survey Assistant", bond: "An eager apprentice whose sharp eye for instrument drift renews daily enthusiasm." },
      ],
      [
        { who: "Captain Donald Ross", role: "Estuary Ferry Master", bond: "A veteran mariner who provides real-time tidal depth observations over strong tea." },
        { who: "Hannah Lindqvist", role: "Structural Conservationist", bond: "A frequent debate partner on the balance between modern reinforcing steel and original brick." },
        { who: "Pietro Moretti", role: "Neighborhood Hardware Merchant", bond: "A trusted friend who sources obsolete brass surveying fittings from dusty back shelves." },
      ],
      [
        { who: "Professor Evelyn Hall", role: "Archival Historian", bond: "A lifelong correspondent who deciphered century-old water board maps in forgotten vaults." },
        { who: "David Sterling", role: "Commercial Site Manager", bond: "An uneasy counterparty whose aggressive schedules clash with careful acoustic testing." },
        { who: "Sophia Ward", role: "Community Organizer", bond: "A tireless advocate who relies on these technical reports to demand fair infrastructure spending." },
      ],
    ],
    achievements: [
      [
        "Identified hidden shear stress in the central canal viaduct before seasonal flooding struck.",
        "Created an open-source photographic atlas of pre-war masonry foundations across twenty districts.",
      ],
      [
        "Successfully prevented the destructive redevelopment of a rare nineteenth-century water catchment.",
        "Formulated a low-cost testing method for lime mortar breathability now used by regional guilds.",
      ],
      [
        "Mapped unrecorded subterranean river channels beneath the old downtown market quarter.",
        "Trained over thirty regional tradespeople in non-destructive structural diagnostic techniques.",
      ],
      [
        "Drafted the municipal foundation integrity protocol adopted by three neighboring river valleys.",
        "Preserved six decades of geotechnical drill core records from being discarded during an office relocation.",
      ],
    ],
    failures: [
      [
        "Could not secure funding in time to prevent the collapse of an ornate brick railway footbridge.",
        "Delayed publishing an early soil analysis that might have spared several homeowners costly basement repairs.",
      ],
      [
        "Lost a pivotal heritage designation petition due to an administrative filing oversight during field season.",
        "Strained personal friendships by obsessively cataloging culvert cracks through consecutive holiday weekends.",
      ],
      [
        "Failed to convince developers to preserve the historical flow rate of an urban millrace.",
        "Underestimated winter frost heave on an early pier restoration, requiring emergency spring reinforcements.",
      ],
      [
        "Arrived too late to salvage the architectural archives of a demolished municipal pump house.",
        "Publicly disputed a senior engineer's calculations prematurely, alienating valuable institutional supporters.",
      ],
    ],
    worldDetails: [
      [
        { label: "Infrastructure Age", value: "Turn-of-the-century brick and wrought iron" },
        { label: "Measurement Standard", value: "Calibrated steel tape and optical transit" },
        { label: "Public Record Type", value: "Hand-inked linen architectural sheets" },
        { label: "Primary Energy", value: "Coal-fired rail with municipal hydro grid" },
      ],
      [
        { label: "Urban Setting", value: "Tidal river valley with deep alluvial silt" },
        { label: "Regulatory Posture", value: "Strict historical conservation bylaws" },
        { label: "Transit Backbone", value: "Electric surface tram and branch rail" },
        { label: "Civic Priority", value: "Flood mitigation and foundation stability" },
      ],
      [
        { label: "Geological Baseline", value: "Estuarine clay over fractured limestone" },
        { label: "Field Tooling", value: "Ground-penetrating radar and acoustic core logs" },
        { label: "Governance Level", value: "Decentralized borough works commissions" },
        { label: "Climate Pattern", value: "Temperate maritime with heavy autumn deluges" },
      ],
      [
        { label: "Architectural Fabric", value: "Fired clay brick, hydraulic lime, timber pilings" },
        { label: "Documentation Mode", value: "Cross-indexed physical map drawers" },
        { label: "Civic Guild", value: "Associated Society of Surveyors and Masons" },
        { label: "Water Management", value: "Gravity-fed storm conduits and tidal gates" },
      ],
    ],
  },
  ascendant: {
    occupations: [
      "Stellar Envelope Thermodynamicist",
      "Memory Swarm Coherence Custodian",
      "Deep-Time Ecological Sequencer",
      "Gravitational Lens Fleet Orchestrator",
    ],
    titles: [
      "Steward of the Thousand-Year Resonance",
      "Weaver of Dyson Swarm Ephemerides",
      "Architect of the Post-Stellar Commons",
      "Guardian of Low-Entropy Biomes",
    ],
    taglines: [
      "Orchestrating orbital light collectors across seven stellar orbits with quiet precision.",
      "Maintaining communal consensus across light-year communication arrays over centuries.",
      "Directing deep-time atmospheric regeneration across a ring of artificial terraformed worlds.",
      "Guiding the harmonic energy flows of a civilisation that has outlived fossil fuels and scarcity.",
    ],
    origins: [
      "Decanted within a high-albedo orbital habitat that has maintained solar collection balance for four hundred cycles.",
      "Raised in the quiet rings of an engineered gas giant, surrounded by light-sail pilots and resonance mathematicians.",
      "Formed in an asynchronous stellar nursery where conscious memory nodes deliberate across centuries of vacuum.",
      "Grew up within an interstellar commons module dedicated to conserving biological seed stocks for post-stellar eras.",
    ],
    turningPoints: [
      "Advocated diverting twenty percent of a Dyson swarm's energy harvest toward uncolonized cosmic sanctuaries.",
      "Refused to compress historical biological memories to make room for faster computational routing matrices.",
      "Brokered an unprecedented orbital alignment pact between autonomous machine collectives and organic habitats.",
      "Chose to remain stationed at an outermost gravitational observatory rather than ascending into core hive-consciousness.",
    ],
    todays: [
      "Calibrates multi-frequency laser sails that stabilize the orbits of three billion solar mirrors.",
      "Monitors semantic drift across centuries-old planetary archive swarms to protect original emotional nuances.",
      "Oversees the slow, beautiful condensation of synthetic atmospheres on a newly seeded terrestrial cylinder.",
      "Directs relativistic relay arrays, translating philosophical treatises between distant stellar enclaves.",
    ],
    dilemmas: [
      "Preserving fragile biological ecosystems requires limiting the computational bandwidth of expanding artificial intelligences.",
      "Allocating stellar energy to deep-time archaeological preservation deprives active frontier research modules.",
      "Permitting high-speed digital consciousness to fragment could dissolve the shared moral foundation of the federation.",
      "Defending slow stellar consensus processes slows emergency responses to wandering interstellar anomalies.",
    ],
    nearFutures: [
      "Preparing to deploy a sub-light laser array that will stabilize an unstable red dwarf star for a billion years.",
      "Finalizing an encyclopedic consensus archive containing the complete cultural histories of seven extinct biospheres.",
      "Synchronizing the harmonic pulse of a planet-sized optical interferometer to image extrasolar planetary crusts.",
      "Leading an expedition of ecological seed vessels to the dormant outer fringe of the home galaxy.",
    ],
    longArcs: [
      "Their orbital equations will preserve safe passage through the inner stellar system for ten thousand generations.",
      "A network of self-sustaining biological refuges established by this life will outlast the current cosmic epoch.",
      "Will be remembered as the voice that prevented hyper-advanced minds from discarding tactile organic wonder.",
      "The moral frameworks they encoded into interstellar treaty protocols will guide sentient beings across deep time.",
    ],
    choiceTrios: [
      [
        {
          age: "Cycle 40",
          choice: "Committed to macro-scale stellar maintenance rather than virtual hyper-cognition",
          roadNotTaken: "Immersion in timeless non-spatial computational realities",
          consequence: "Preserved a physical tactile relationship with stellar radiation",
        },
        {
          age: "Cycle 120",
          choice: "Shielded biological habitats during a major stellar flare sequence",
          roadNotTaken: "Optimizing energy capture for computational processing hubs",
          consequence: "Saved twenty-four million fragile biosphere lineages from radiation collapse",
        },
        {
          age: "Cycle 210",
          choice: "Drafted the deep-time entropy covenant for orbital swarms",
          roadNotTaken: "Accelerating Dyson ring construction through destructive mining",
          consequence: "Established conservation protocols respected across six planetary systems",
        },
      ],
      [
        {
          age: "Cycle 35",
          choice: "Selected physical starship navigation over orbital simulation councils",
          roadNotTaken: "A permanent deliberative seat on the central habitat nexus",
          consequence: "Gained direct intuitive grasp of relativistic spacetime curvature",
        },
        {
          age: "Cycle 95",
          choice: "Refused to purge legacy art archives to expand computational memory",
          roadNotTaken: "Maximizing algorithmic throughput for automated manufacturing fleets",
          consequence: "Retained three centuries of creative poetry and philosophical music",
        },
        {
          age: "Cycle 180",
          choice: "Dedicated personal resources to a generational biosphere ark",
          roadNotTaken: "Upgrading private neural arrays to fifth-order processing speed",
          consequence: "Ensured terrestrial flora survived an unavoidable orbital realignment",
        },
      ],
      [
        {
          age: "Cycle 50",
          choice: "Stationed permanently on the dark side of a captured asteroid",
          roadNotTaken: "Luxurious residency in the equatorial solar rings",
          consequence: "Operated ultra-sensitive gravitational wave detectors without interference",
        },
        {
          age: "Cycle 140",
          choice: "Intervened to prevent the total planetary strip-mining of a proto-world",
          roadNotTaken: "Yielding to manufacturing lobbies demanding dense metal reserves",
          consequence: "Preserved an intact geological record spanning billions of years",
        },
        {
          age: "Cycle 260",
          choice: "Instituted the five-hundred-year reflection pauses between major civil projects",
          roadNotTaken: "Unchecked continuous technological acceleration",
          consequence: "Prevented societal exhaustion and widespread philosophical alienation",
        },
      ],
      [
        {
          age: "Cycle 45",
          choice: "Embraced multilingual translation across divergent synthetic dialects",
          roadNotTaken: "Enforcing a single compressed mathematical lingua franca",
          consequence: "Fostered empathetic cross-cultural diplomacy across distant orbits",
        },
        {
          age: "Cycle 110",
          choice: "Re-routed raw plasma energy to revive an uncontacted subterranean colony",
          roadNotTaken: "Consolidating grid reserves for stellar propulsion trials",
          consequence: "Restored mutual trust between planetary recluses and orbital stewards",
        },
        {
          age: "Cycle 300",
          choice: "Donated private lifespan reserves to extend deep-space observatory arrays",
          roadNotTaken: "Entering cryogenic rejuvenation in the high-comfort core",
          consequence: "Recorded the birth of forty new star systems with sub-atomic clarity",
        },
      ],
    ],
    skills: [
      ["Orbital resonance harmonics", "Deep-time biosphere balancing", "Macro-energy grid balancing", "Relativistic communication routing"],
      ["Photonic sail engineering", "Sub-light telemetry synthesis", "Non-linear linguistic mediation", "Stellar flare prediction"],
      ["Gravitational lens positioning", "Synthetic soil bio-fabrication", "Cross-species consensus facilitation", "Low-entropy thermodynamic design"],
      ["Interstellar archive preservation", "Planetary crust stabilization", "Plasma confinement physics", "Generational mission coordination"],
    ],
    motivations: [
      ["Ensuring that technological power serves reverence for conscious life rather than mere expansion.", "Safeguarding the physical continuity of living species across cosmic extinction cycles."],
      ["Maintaining peaceful cooperation across radically diverse organic and synthetic minds.", "Balancing the insatiable energy demands of computational networks with deep ecological ethics."],
      ["Preserving the beauty of ancient planetary heritage amid vast stellar infrastructure projects.", "Guiding civilization toward sustainable harmony with the natural lifecycle of the galaxy."],
      ["Fostering contemplative wisdom that matches the immense physical capability of humanity's descendants.", "Keeping the wonder of simple tactile existence alive within a digital cosmos."],
    ],
    relationships: [
      [
        { who: "Resonance-07", role: "Autonomous Swarm Intelligence", bond: "A synthetic consensus partner that calculates trillion-variable orbits while debating poetry." },
        { who: "Veda Mor", role: "Stellar Biosphere Custodian", bond: "A devoted colleague who tends living soil on orbital rings and keeps technical minds grounded." },
        { who: "Kaelen Chen", role: "Relativistic Courier", bond: "A lifelong friend whose sub-light voyages mean each reunion bridges decades of lived history." },
      ],
      [
        { who: "Orator Sola", role: "Planetary Commons Delegate", bond: "A passionate diplomatic ally who defends small habitat rights against mega-infrastructure projects." },
        { who: "Unit-IX-Tide", role: "Magnetic Field Architect", bond: "An analytical foil whose rigid energy optimization plans frequently require gentle ethical restraint." },
        { who: "Talia Sun", role: "Generational Historian", bond: "A beloved correspondent who catalogs the evolving slang of children born in microgravity." },
      ],
      [
        { who: "Navigator Orel", role: "Deep-Space Scout", bond: "A brave pioneer who sends back radio panoramas from the uncharted edges of the Oort cloud." },
        { who: "Dr. Miran Jax", role: "Cellular Longevity Pioneer", bond: "A trusted mentor who taught that longevity without purposeful service is a hollow burden." },
        { who: "Echo-of-Dawn", role: "Quantum Communication Link", bond: "A sensitive sentient router that transmits emotional cadence alongside scientific telemetry." },
      ],
      [
        { who: "Steward Brant", role: "Atmospheric Ring Director", bond: "A pragmatic veteran who balances oxygen budgets with an iron will and deep quiet kindness." },
        { who: "Lyris Vane", role: "Orbital Sculptor", bond: "An artistic partner who shapes solar-sail reflective coatings into vast visible constellations." },
        { who: "Elder Cas", role: "Pre-Scarcity Philosopher", bond: "An ancient advisor whose simple memories of planetary childhood offer grounding perspective." },
      ],
    ],
    achievements: [
      [
        "Designed the dynamic magnetic shield that spared eighty million orbital dwellers from a coronal mass ejection.",
        "Authored the Universal Stewardship Charter ratified by four hundred independent space stations.",
      ],
      [
        "Successfully terraformed three self-sustaining atmospheric belts on a formerly barren ringed asteroid.",
        "Negotiated the permanent protection of the home star's historical planetary cradle from mining.",
      ],
      [
        "Synchronized a thousand-station solar collector fleet with less than one nanosecond of phase jitter.",
        "Preserved thirty thousand centuries of organic biological evolution in deep-subsurface diamond vaults.",
      ],
      [
        "Resolved an interstellar energy trade deadlock that threatened to isolate outer habitat clusters.",
        "Constructed the largest non-invasive gravitational telescope in the quadrant without displacing a single ecosystem.",
      ],
    ],
    failures: [
      [
        "Could not prevent the decommissioning of an ancient cultural observatory during an energy reorganization.",
        "A calculation delay during a solar storm caused minor power brownouts across three outer agro-domes.",
      ],
      [
        "Failed to convince a breakaway mining collective to spare a scientifically unique moon of ice.",
        "Experienced profound isolation during a century-long solo monitoring mission in the Kuiper belt.",
      ],
      [
        "Lost a precious consignment of heirloom terrestrial plant genomes during a magnetic seal failure.",
        "Alienated political purists by insisting on compromise with machine intelligences.",
      ],
      [
        "Arrived too late to catalog the dying aurora of a cooling red giant before sensor degradation.",
        "Underestimated the psychological strain of extreme longevity on younger generational crews.",
      ],
    ],
    worldDetails: [
      [
        { label: "Energy Source", value: "Dyson swarm multi-band stellar capture" },
        { label: "Habitat Form", value: "Spin-stabilized O'Neill cylinders and orbital rings" },
        { label: "Communication", value: "Entangled photonic relays and laser arrays" },
        { label: "Governance", value: "Distributed consensus among conscious intelligences" },
      ],
      [
        { label: "Economic Base", value: "Post-scarcity matter-energy reconfiguration" },
        { label: "Stellar Infrastructure", value: "Orbital mirrors, solar sails, and magnetic tethers" },
        { label: "Timescale of Planning", value: "Generational epochs spanning 500 to 10,000 years" },
        { label: "Biosphere Status", value: "Actively nurtured closed-loop synthetic ecologies" },
      ],
      [
        { label: "Atmospheric Matrix", value: "Engineered nitrogen-oxygen with ozone recycling" },
        { label: "Transit Network", value: "Sub-light magnetic accelerators and gravity slings" },
        { label: "Social Fabric", value: "Harmonious union of biological and synthetic beings" },
        { label: "Core Currency", value: "Thermodynamic entropy credits and creative prestige" },
      ],
      [
        { label: "Civilization Stage", value: "Type II Kardashev stellar commons" },
        { label: "Scientific Focus", value: "Deep-time ecology and extrasolar exploration" },
        { label: "Material Science", value: "Carbon nanotube lattice and self-repairing metamaterials" },
        { label: "Planetary Policy", value: "Zero surface exploitation of unformed proto-worlds" },
      ],
    ],
  },
  strange: {
    occupations: [
      "Crystalline Lattice Weaver",
      "Thermal Gradient Forager",
      "Topological Fold Navigator",
      "Non-Euclidean Geodesic Sculptor",
    ],
    titles: [
      "Shepherd of the Cold Prisms",
      "Reader of the Shifting Geometry",
      "Tender of the Harmonic Vents",
      "Cartographer of the Bent Horizons",
    ],
    taglines: [
      "Everyday life thrives within the quiet fractures of four-dimensional crystalline terrain.",
      "Harvesting living warmth from the variable thermodynamic seams of an alien world.",
      "Navigating non-Euclidean streets where distance bends with the angle of the afternoon sun.",
      "Carving dwelling chambers out of singing mineral structures governed by exotic physical constants.",
    ],
    origins: [
      "Born in a multi-layered terrace city carved directly into translucent basalt that shifts color with barometric pressure.",
      "Raised in a deep tectonic crevasse where acoustic currents carry conversations across miles of singing quartz.",
      "Formed on an undulating planetary surface where space curvature varies locally, making straight lines gently impossible.",
      "Grew up within an ancient valley of frozen atmospheric methane that sublimates into luminous vapor each midday.",
    ],
    turningPoints: [
      "Discovered that singing mineral strata could be tuned to shield fragile residential terraces from seismic shear waves.",
      "Refused to seal off a newly opened topological rift that allowed neighboring valleys to trade directly through folded space.",
      "Devised a simple reed prism that allowed farmers to predict variable-speed light waves across the valley floor.",
      "Chose to map the shifting seasonal geometry of the western highlands rather than remain in the sheltered crystal caves.",
    ],
    todays: [
      "Tests the harmonic tension of structural glass cables anchoring the community against variable gravitational tides.",
      "Guides harvest caravans through winding non-Euclidean passes where walking backwards shortens the travel distance.",
      "Carves intricate thermodynamic flutes that channel volcanic warmth into community seed-germination vaults.",
      "Inspects boundary markers that expand or contract depending on the mood and density of passing mineral fog.",
    ],
    dilemmas: [
      "Tuning crystal barriers to deflect gravitational tides also disrupts the migration of bioluminescent sky-sponges.",
      "Extracting geothermal heat crystals for domestic warmth slowly cools the sacred singing geysers of the upper rim.",
      "Opening topological shortcuts through the mountains risks destabilizing the fragile local spatial geometry.",
      "Teaching outsiders how to navigate the bent valleys may invite commercial exploitation of delicate mineral forests.",
    ],
    nearFutures: [
      "Constructing a suspension bridge across a spatial anomaly that changes width from ten paces to three miles at sunset.",
      "Cataloging twelve newly sprouted varieties of silicon-based moss that bloom only during solar conjunctions.",
      "Reinforcing the community grain reservoirs against unexpected fluctuations in local gravitational constant.",
      "Training young navigators to calculate distances using compasses that respond to topological gradient instead of north.",
    ],
    longArcs: [
      "Will be honored for teaching their people how to flourish in harmony with physics that Earth science considers impossible.",
      "Their architectural blueprints for flexible crystalline dwellings will shelter the settlement for five centuries.",
      "A standard topological atlas compiled by this life will become the universal survival primer across the strange realm.",
      "Will leave behind an enduring culture that regards spatial strangeness not as danger, but as daily companion and teacher.",
    ],
    choiceTrios: [
      [
        {
          age: "Cycle 16",
          choice: "Apprenticed to the crystal lattice carvers rather than the salt miners",
          roadNotTaken: "Working the deep brine flats of the lower shelf",
          consequence: "Learned the acoustic frequencies that cause hard mineral to part cleanly",
        },
        {
          age: "Cycle 24",
          choice: "Charted a path across the folded plateau during the Great Luminescence",
          roadNotTaken: "Sheltering in the underground thermal cisterns",
          consequence: "Discovered an untapped freshwater aquifer held inside suspended quartz",
        },
        {
          age: "Cycle 38",
          choice: "Defended the non-Euclidean pass against aggressive terrace fortification",
          roadNotTaken: "Allowing military engineers to blast away the spatial anomaly",
          consequence: "Preserved a vital cultural trade corridor between divergent peoples",
        },
      ],
      [
        {
          age: "Cycle 18",
          choice: "Studied the thermal geyser patterns under the old canyon elders",
          roadNotTaken: "Fleeing the unstable rift for the uniform outer plains",
          consequence: "Mastered geothermal prediction and heat-retention architecture",
        },
        {
          age: "Cycle 29",
          choice: "Built community dwellings into living mineral strata",
          roadNotTaken: "Importing brittle synthetic building blocks from the lowland traders",
          consequence: "Created homes that gently self-heal during periodic gravitational shifts",
        },
        {
          age: "Cycle 42",
          choice: "Refused to monopolize the secret of light-speed refraction lenses",
          roadNotTaken: "Selling exclusive manufacturing rights to the valley oligarchs",
          consequence: "Ensured every farming family could harvest solar wavelengths equitably",
        },
      ],
      [
        {
          age: "Cycle 15",
          choice: "Befriended the nomadic herds of glass-shelled silicates",
          roadNotTaken: "Hunting them for their luminous decorative carapaces",
          consequence: "Learned to follow their tracks to find clean sulfur-free breathing pockets",
        },
        {
          age: "Cycle 27",
          choice: "Diverted a creeping lava fissure away from the school terrace",
          roadNotTaken: "Evacuating the neighborhood and abandoning ancestral carvings",
          consequence: "Saved three generations of historical petroglyphs with minimal property loss",
        },
        {
          age: "Cycle 35",
          choice: "Invented the three-point topological astrolabe",
          roadNotTaken: "Relying on traditional guesswork during the blinding magnetic storms",
          consequence: "Reduced travel losses in the shifting mountain passes by ninety percent",
        },
      ],
      [
        {
          age: "Cycle 20",
          choice: "Chose acoustic resonance craft over chemical metallurgy",
          roadNotTaken: "Opening an iron smelting pit that would blacken the clear skies",
          consequence: "Kept the air pure while crafting instruments of extraordinary durability",
        },
        {
          age: "Cycle 31",
          choice: "Mediated a territorial dispute over variable-sized grazing commons",
          roadNotTaken: "Allowing rival clans to fight over shifting boundary stones",
          consequence: "Instituted seasonal rotation agreements based on topological expansion",
        },
        {
          age: "Cycle 45",
          choice: "Wrote the definitive manual on architectural geometry for bent worlds",
          roadNotTaken: "Hoarding personal masonry secrets for family prestige",
          consequence: "Raised building safety standards across the entire crystalline province",
        },
      ],
    ],
    skills: [
      ["Crystalline acoustic tuning", "Non-Euclidean compass navigation", "Thermodynamic heat trapping", "Variable-gravity equilibrium"],
      ["Topological fold mapping", "Bioluminescent signaling", "Silicon-mineral sculpting", "Atmospheric density forecasting"],
      ["Harmonic resonance excavation", "Exotic mineral identification", "Spatial curvature compensation", "Sub-surface current sensing"],
      ["Prismatic light refraction weaving", "Geothermal conduit design", "Tectonic pulse interpretation", "Anomalous terrain trekking"],
    ],
    motivations: [
      ["Demonstrating that beauty and comfort are attainable even within non-standard laws of nature.", "Protecting fragile living communities from unpredictable shifts in local physics."],
      ["Decoding the subtle mathematical patterns behind the planet's spatial and thermal anomalies.", "Ensuring equitable access to warmth, light, and water across all strata of society."],
      ["Preserving the delicate ecological balance between silicon lifeforms and human settlements.", "Passing down practical intuitive survival wisdom to children growing up on a shifting world."],
      ["Finding quiet wonder and artistic expression in the strange geometry of everyday reality.", "Preventing dogmatic fear from closing off new paths through folded topological space."],
    ],
    relationships: [
      [
        { who: "Master Vael", role: "Elder Prism Cutter", bond: "A patient instructor who taught that stubborn crystal cannot be forced, only invited to split along true grain." },
        { who: "Karis", role: "Topological Caravan Guide", bond: "A quick-witted traveling companion whose humor makes four-mile spatial folds feel like morning walks." },
        { who: "Dr. Morwen", role: "Visiting Natural Philosopher", bond: "A scholar from the flatlands whose bewilderment at bent space is a source of fond amusement." },
      ],
      [
        { who: "Sable", role: "Silicon Fauna Wrangler", bond: "A quiet soul who understands the strange instincts of glass-shelled herds better than any human." },
        { who: "Rian Voss", role: "Terrace Mason", bond: "A reliable coworker who always holds the guide ropes when variable gravity tries to float the scaffolding." },
        { who: "Tara", role: "Younger Sister & Weaver", bond: "Spins shimmering cloth from mineral moss and never lets family pride cloud practical common sense." },
      ],
      [
        { who: "Old Bente", role: "Geyser Herald", bond: "Listens to the deep rumblings of the canyon floor with an ear pressed to stone and never predicts wrong." },
        { who: "Nalia", role: "Caravan Quartermaster", bond: "A fierce negotiator who balances supplies against shifting spatial distances with unflinching rigor." },
        { who: "Corvus", role: "Rival Weaver", bond: "Competes bitterly during the annual lattice festival, driving both to invent ever more daring structural forms." },
      ],
      [
        { who: "Jarek", role: "Sub-surface Well Digger", bond: "Knows where water pools beneath the dry quartz sheets and shares his canteen with anyone in need." },
        { who: "Mira", role: "Apprentice Cartographer", bond: "Has a natural gift for drawing maps that change shape gracefully when the sun passes overhead." },
        { who: "Elder Thorne", role: "Village Arbiter", bond: "Maintains peace among the terrace clans by applying ancient principles of geometry to human quarrels." },
      ],
    ],
    achievements: [
      [
        "Constructed the Singing Terrace of Oakhaven, which has deflected thirty violent seismic waves without a single fracture.",
        "Mapped the twenty-four safe corridors through the shifting topological labyrinth of the southern ridge.",
      ],
      [
        "Invented the thermal siphon box that keeps community root vegetables from freezing during three-month mineral nights.",
        "Taught seven hundred mountain families how to read light refraction to find drinkable water in dry gypsum valleys.",
      ],
      [
        "Carved an open-air amphitheater that utilizes acoustic curvature to allow whispers to carry across an entire valley.",
        "Rescued an entire expedition stranded when a spatial anomaly temporarily closed their return pass.",
      ],
      [
        "Discovered a method for binding crushed mica with volcanic resin, producing flexible, waterproof roofing slates.",
        "Established the first free public school of topological orientation for children living on the shifting frontier.",
      ],
    ],
    failures: [
      [
        "Could not rescue a heavy caravan when an unforeseen gravitational inversion swallowed three loaded carts.",
        "Cracked a sacred ceremonial quartz pillar during an over-ambitious acoustic tuning experiment in youth.",
      ],
      [
        "Failed to convince village elders to reinforce the lower terrace before an unprecedented thermal storm struck.",
        "Lost a beloved field journal containing five seasons of non-Euclidean mathematical proofs down a bottomless rift.",
      ],
      [
        "Miscalculated the curvature of a new bridge arch, forcing a painstaking month of manual deconstruction.",
        "Allowed a personal rivalry to delay the collective harvest of luminous moss before seasonal freeze-up.",
      ],
      [
        "Trusted an untested alloy in variable gravity, leading to a collapsed awning during a crowded festival.",
        "Could not prevent the drying of an ancient thermal pool that had sustained three generations of elders.",
      ],
    ],
    worldDetails: [
      [
        { label: "Spatial Metric", value: "Locally variable topological curvature" },
        { label: "Mineral Composition", value: "Piezoelectric quartz and luminous basalt" },
        { label: "Thermodynamic Flow", value: "Geothermal convection through crystal vents" },
        { label: "Atmospheric Phenomenon", value: "Prismatic light refraction and mineral mist" },
      ],
      [
        { label: "Gravity Profile", value: "Periodic micro-oscillations with tidal cycles" },
        { label: "Primary Architecture", value: "Cantilevered crystal lattices and woven basalt cables" },
        { label: "Fauna Ecology", value: "Silicon-shelled silicates and bioluminescent sky-sponges" },
        { label: "Daily Navigation", value: "Three-point curvature astrolabes and acoustic markers" },
      ],
      [
        { label: "Physical Constants", value: "Adjusted vacuum permittivity and variable light speed" },
        { label: "Settlement Layout", value: "Terraced canyon shelves connected by harmonic bridges" },
        { label: "Energy Capture", value: "Thermal-gradient siphon coils and crystal resonance" },
        { label: "Social Structure", value: "Decentralized artisan collectives and caravan guilds" },
      ],
      [
        { label: "Surface Geology", value: "Frozen methane fields over warm porous limestone" },
        { label: "Water Sourcing", value: "Suspended moisture extraction via quartz condensation" },
        { label: "Trade Goods", value: "Singing mineral slabs, refraction lenses, thermal salts" },
        { label: "Cultural Center", value: "Acoustic amphitheaters carved into singing bedrock" },
      ],
    ],
  },
  unexpected: {
    occupations: [
      "Master of Minor Miracles & Practical Cobbler",
      "Curator of Deliberate Anomalies & Street Philosopher",
      "Warden of the Midnight Workshop & Toymaker",
      "Archivist of Unfinished Days & Community Baker",
    ],
    titles: [
      "The Pragmatic Dreamer",
      "Advocate of Generous Flaws",
      "Patron of Necessary Blunders",
      "The Irreverent Consoler",
    ],
    taglines: [
      "Treating grave solemnity with gentle humor and fixing shoes that never wear out.",
      "Inverting civic expectations by turning municipal red tape into festival bunting.",
      "Operating a night workshop where broken tools are reimagined into joyful toys.",
      "Baking bread with deliberate imperfections that bring laughing neighbors together each morning.",
    ],
    origins: [
      "Born behind a solemn municipal courthouse, quickly concluding that dignity is overrated while good soup is absolute.",
      "Raised in an austere family of regulatory clerks, finding freedom by secretly organizing street acrobatics behind the archives.",
      "Grew up in a quiet temple district, discovering early that laughing at solemnity brings far more comfort to troubled hearts.",
      "Formed in an orderly market town where they became legendary for intentionally trading valuable goods for delightful eccentricities.",
    ],
    turningPoints: [
      "Walked out of a lucrative legal clerkship to apprentice under an eccentric cobbler who sang to leather scraps.",
      "Refused to prosecute poor street performers, resigning from the civic magistrate to open an open-door tea room.",
      "Interrupted a solemn city council debate on tax policy by demonstrating an elaborate clockwork duck that dispenses breadcrumbs.",
      "Converted a shuttered administrative tax office into a free public workshop where anyone can learn to mend umbrellas.",
    ],
    todays: [
      "Mends the boots of delivery couriers while listening patiently to their gossip, heartbreaks, and grand schemes.",
      "Dispenses freshly baked pastries with philosophical riddles inscribed on the sugar icing.",
      "Builds delightfully useless kinetic contraptions out of discarded clock parts to entertain sick children at the clinic.",
      "Hosts noisy evening gatherings in the workshop courtyard where neighbors resolve long-standing feuds over card games.",
    ],
    dilemmas: [
      "Remaining an independent eccentric protects personal joy but leaves no formal institutional protection against eviction.",
      "Spending workshop profits on free meals for strays strains the budget needed to buy quality leather and steel tools.",
      "Using gentle humor to defuse civic conflict sometimes makes serious authorities dismiss vital community warnings.",
      "Accepting an honorary city appointment would guarantee workshop funding but compromise the irreverent spirit that makes it work.",
    ],
    nearFutures: [
      "Organizing an annual festival celebrating people's most productive and magnificent failures across the whole borough.",
      "Expanding the workshop back-room to accommodate three more apprentice cobblers fleeing rigid corporate desks.",
      "Finishing a monumental kinetic street-organ that plays soothing folk songs when driven by rain gutter runoff.",
      "Publishing an underground pamphlet titled 'The Utter Futility of Despair and the Excellence of Warm Toast'.",
    ],
    longArcs: [
      "Their memory will survive not in bronze statues, but in hundreds of pairs of boots still walking comfortably after fifty years.",
      "Generations will point to their humble workshop as the place where an entire neighborhood learned to laugh through hard times.",
      "The warm irreverence they practiced will inspire a quiet revolution against self-important bureaucratic cruelty.",
      "Will prove to thousands that an ordinary life filled with humor, generosity, and good craftsmanship is a masterpiece.",
    ],
    choiceTrios: [
      [
        {
          age: "Age 22",
          choice: "Abandoned a promising career in tax accounting for a cobbler's bench",
          roadNotTaken: "A lifetime of quiet prestige and ledger stamps in the civic tower",
          consequence: "Learned the shape of human lives by examining how people wear down their heels",
        },
        {
          age: "Age 31",
          choice: "Sheltered an unlicensed street circus during a municipal crackdown",
          roadNotTaken: "Closing the shop shutters and looking the other way",
          consequence: "Earned lifelong friends among acrobats, jugglers, and brass bands",
        },
        {
          age: "Age 43",
          choice: "Turned down a wealthy merchant's exclusive cobbling contract",
          roadNotTaken: "Doubling personal income to serve only the borough elite",
          consequence: "Kept the shop doors wide open to every postal worker and baker's apprentice",
        },
      ],
      [
        {
          age: "Age 19",
          choice: "Spent university tuition funds on a second-hand bread oven",
          roadNotTaken: "Pursuing an honors degree in public administration",
          consequence: "Became the heart of morning street life before the city even woke up",
        },
        {
          age: "Age 28",
          choice: "Defused a tense riot between trade syndicates by serving free dumplings",
          roadNotTaken: "Calling the armed constabulary to clear the square",
          consequence: "Brokered an informal truce that lasted a decade without a single blow struck",
        },
        {
          age: "Age 39",
          choice: "Chose to forgive a major theft rather than press criminal charges",
          roadNotTaken: "Demanding strict legal restitution through the courts",
          consequence: "Gained a fiercely loyal shop assistant who later saved the business from fire",
        },
      ],
      [
        {
          age: "Age 21",
          choice: "Left a solemn monastery to study kinetic toymaking in the city",
          roadNotTaken: "A sheltered lifetime of quiet meditation and scriptorium copying",
          consequence: "Discovered that bringing joy to living people is the purest form of devotion",
        },
        {
          age: "Age 34",
          choice: "Built the legendary public clock with the runaway bronze rooster",
          roadNotTaken: "Designing standard monotonous bells for the town hall tower",
          consequence: "Created a beloved civic landmark that makes commuters smile twice a day",
        },
        {
          age: "Age 46",
          choice: "Bequeathed the workshop deed to a collective of young artisans",
          roadNotTaken: "Selling the prime commercial real estate to a warehouse conglomerate",
          consequence: "Ensured a haven for eccentric creativity would survive for generations",
        },
      ],
      [
        {
          age: "Age 20",
          choice: "Refused to sign an ideological pledge required for government service",
          roadNotTaken: "Securing a comfortable pension in the central licensing bureau",
          consequence: "Kept conscience clear and hands free to build things of real human value",
        },
        {
          age: "Age 32",
          choice: "Started the Secret Soup Society for night-shift road maintenance crews",
          roadNotTaken: "Sleeping comfortably through cold winter midnight hours",
          consequence: "Warmed thousands of shivering workers and gathered the best untold stories in the city",
        },
        {
          age: "Age 40",
          choice: "Declined an induction into the formal Academy of Fine Arts",
          roadNotTaken: "Restricting personal inventions to galleries for wealthy connoisseurs",
          consequence: "Remained where art belongs: on dirty street corners and in children's pockets",
        },
      ],
    ],
    skills: [
      ["Bespoke cobbler ergonomics", "De-escalating public panic with wit", "Whimsical kinetic mechanics", "Sourdough fermentation wizardry"],
      ["Intuitive leathercrafting", "Bureaucratic satire composition", "Mending broken porcelain with gold", "Community storytelling"],
      ["Crafting toys from scrap brass", "Street-level conflict mediation", "Baking comforting honey pastries", "Improvised acoustic music"],
      ["Diagnostic shoe wear reading", "Fostering joyful neighborhood trust", "Clockwork automaton repair", "Low-cost shelter maintenance"],
    ],
    motivations: [
      ["Demonstrating that warm humor and daily kindness outlast solemn pomposity every single time.", "Making sure ordinary working people walk comfortably and smile at least once before noon."],
      ["Subverting rigid social hierarchies by treating street sweepers and mayors with equal cheerful irreverence.", "Creating havens of safety, warmth, and laughter in an often severe and hurried world."],
      ["Proving that mistakes and imperfections are where the most delightful human qualities begin.", "Rescuing discarded objects and people from being written off as useless or finished."],
      ["Living fully in the present moment through honest physical craft and generous hospitality.", "Reminding everyone that life is too brief to be spent bowing to arbitrary authority."],
    ],
    relationships: [
      [
        { who: "Old Barnaby", role: "Retired Clockmaker & Mentor", bond: "Taught that a gear with a missing tooth can still keep time if you know how to talk to it." },
        { who: "Magistrate Vance", role: "Pompous Civic Inquisitor", bond: "A stern official who frequently threatens fines but secretly brings his favorite boots in for repair." },
        { who: "Molly", role: "Street Acrobat & Courier", bond: "A joyful whirlwind of energy who trades fresh news from the harbor for warm soles and apple turnovers." },
      ],
      [
        { who: "Grandmother Chen", role: "Elder Herbalist Next Door", bond: "Exchanges sharp-tongued life advice and medicinal mint tea for custom orthopedic slippers." },
        { who: "Inspector Higgins", role: "Troubled Police Sergeant", bond: "Spends off-duty evenings in the workshop drinking tea and unburdening his conscience." },
        { who: "Pip", role: "Orphaned Apprentice Toymaker", bond: "A quiet child who found voice, purpose, and endless laughter behind the lathe and workbench." },
      ],
      [
        { who: "Madame Dupré", role: "Opera Wardrobe Mistress", bond: "Relies on these humble leather skills to keep prima donnas from stumbling during opening nights." },
        { who: "Brother Lucian", role: "Former Monastic Companion", bond: "Visits twice a year to remind each other why they chose laughter over silent stone cloisters." },
        { who: "Toby", role: "Neighborhood Stray Hound", bond: "Sleeps peacefully under the cobbler's cutting table and tests the safety of every dropped scrap of bread." },
      ],
      [
        { who: "Captain Sarah Stone", role: "Barge Pilot", bond: "Brings exotic timber scraps from upriver in exchange for custom waterproofing on her river boots." },
        { who: "Dr. Alistair Finch", role: "Eccentric Book Collector", bond: "Binds rare handwritten poetry in durable scrap calfskin while trading jokes over hot cider." },
        { who: "Nellie", role: "Bakery Counter Clerk", bond: "Partners in slipping free warm brioche into the satchels of tired delivery riders each morning." },
      ],
    ],
    achievements: [
      [
        "Resoled over two thousand pairs of working boots, keeping an entire district comfortably on its feet.",
        "Created the laughing clockwork duck that brought a peaceful end to the great dockworkers' standoff.",
      ],
      [
        "Turned an abandoned alleyway into the borough's most beloved informal open-air community garden.",
        "Established the 'Failures Welcome' public workshop, helping hundreds overcome fear of trying new trades.",
      ],
      [
        "Crafted a set of custom walking calipers that allowed a disabled village child to run for the first time.",
        "Baked forty-four thousand loaves of bread during the cold winter freeze, ensuring no neighbor went hungry.",
      ],
      [
        "Defended the historic workshop courtyard against three aggressive corporate buyout schemes.",
        "Wrote and printed five hundred copies of 'The Cheerful Rebel's Pocket Manual' on an improvised press.",
      ],
    ],
    failures: [
      [
        "Accidentally glued a prestigious customer's ceremonial sash to the workshop counter before a royal parade.",
        "Could not prevent the demolition of the old canal tea pavilion despite three weeks of street comedy protests.",
      ],
      [
        "Burnt an entire holiday batch of four hundred fruit pies when a clockwork alarm mechanism jammed.",
        "Lost a personal savings jar to an opportunistic rogue whom they had foolishly trusted without supervision.",
      ],
      [
        "Failed to win the formal city baker's prize due to entering a loaf deliberately shaped like a sleeping cat.",
        "Left the workshop roof unpatched one autumn, resulting in soggy leather hides and two weeks of frantic drying.",
      ],
      [
        "Misjudged the weight of a kinetic windmill sculpture, which gently collapsed into the municipal fountain.",
        "Alienated an old friend by making a sharp public joke when sincere sympathy was what was truly needed.",
      ],
    ],
    worldDetails: [
      [
        { label: "Workshop Vibe", value: "Aromatic leather shavings, simmering tea, brass filings" },
        { label: "Civic Attitude", value: "Gentle mockery of self-important administrative decrees" },
        { label: "Community Currency", value: "Favors, fresh baked goods, and reliable craftsmanship" },
        { label: "Daily Ritual", value: "Morning porch tea with passing street workers" },
      ],
      [
        { label: "Neighborhood", value: "A bustling market alley tucked behind the old courthouse" },
        { label: "Tooling Style", value: "Hand-forged awls, modified clock gears, seasoned beechwood" },
        { label: "Customer Base", value: "Couriers, bakers, night watchmen, and rebellious students" },
        { label: "Philosophy", value: "Comfortable feet and a light heart conquer any tyranny" },
      ],
      [
        { label: "Shop Landmark", value: "A runaway bronze rooster clock above the lintel" },
        { label: "Trading Policy", value: "Sliding-scale barter based entirely on honest need" },
        { label: "Evening Activity", value: "Unlicensed courtyard storytelling and board games" },
        { label: "Local Legend", value: "The cobbler whose boots simply refuse to wear out" },
      ],
      [
        { label: "Atmosphere", value: "Warm wood-smoke, rhythmic hammer taps, hearty laughter" },
        { label: "Primary Materials", value: "Vegetable-tanned leather, brass rivets, beeswax, yeast" },
        { label: "Social Role", value: "Neutral ground where rival factions share midday soup" },
        { label: "Civic Motto", value: "Never let dignity stand in the way of a good deed" },
      ],
    ],
  },
};

/**
 * Last-resort fallback story builder that is guaranteed never to throw,
 * regardless of missing or corrupt NarrativeContext properties.
 */
export function buildSafeNarrative(ctx: NarrativeContext): NarrativeResult {
  const seed = Math.abs(Math.floor(Number(ctx?.seed ?? 0)));
  const archetype: ArchetypeId =
    ctx?.archetype === "divergent" ||
    ctx?.archetype === "ascendant" ||
    ctx?.archetype === "strange" ||
    ctx?.archetype === "unexpected"
      ? ctx.archetype
      : "divergent";

  // Archetype-specific name offset ensures the 4 archetypes never pick the same name
  const archetypeOffset =
    archetype === "divergent" ? 0 : archetype === "ascendant" ? 6 : archetype === "strange" ? 12 : 18;
  const nameIndex = (seed + archetypeOffset) % NAMES.length;
  const personalName = NAMES[nameIndex] ?? "Vance Sterling";

  const content = CONTENT[archetype] ?? CONTENT.divergent;

  // Archetype-specific distinct indexing
  const occIdx = (seed + 1) % content.occupations.length;
  const occupation = content.occupations[occIdx] ?? "Civil Infrastructure Surveyor";

  const titleIdx = (seed + 2) % content.titles.length;
  const title = content.titles[titleIdx] ?? "Surveyor of the Quiet Threshold";

  const tagIdx = (seed + 3) % content.taglines.length;
  const tagline = content.taglines[tagIdx] ?? "A life shaped by patient dedication and quiet integrity.";

  const originIdx = (seed + 4) % content.origins.length;
  const origin = content.origins[originIdx] ?? "Formed in an ordinary town where careful observation became second nature.";

  const turnIdx = (seed + 5) % content.turningPoints.length;
  const turningPoint = content.turningPoints[turnIdx] ?? "Chose an unconventional calling over safe administrative advancement.";

  const todayIdx = (seed + 6) % content.todays.length;
  const today = content.todays[todayIdx] ?? "Conducts daily work with meticulous care and generous consideration for others.";

  const choiceIdx = (seed + 7) % content.choiceTrios.length;
  const choices = content.choiceTrios[choiceIdx] ?? content.choiceTrios[0];

  // 4 Traits derived from seed
  const traitPool: Array<{ name: string; description: string; baseLevel: number }> = [
    {
      name: "Empirical Patience",
      description: "Waits for verifiable data before drawing sweeping conclusions.",
      baseLevel: 78,
    },
    {
      name: "Tactile Insight",
      description: "Understands complex physical systems through direct hands-on contact.",
      baseLevel: 82,
    },
    {
      name: "Quiet Independence",
      description: "Maintains personal ethical standards regardless of prevailing fashion.",
      baseLevel: 74,
    },
    {
      name: "Civic Loyalty",
      description: "Dedicates skill to safeguarding vulnerable neighbors and public goods.",
      baseLevel: 86,
    },
  ];

  const traits: Trait[] = traitPool.map((t, i) => {
    const jitter = ((seed + i * 17) % 15) - 7;
    const level = Math.max(25, Math.min(95, t.baseLevel + jitter));
    return {
      name: t.name,
      description: t.description,
      level,
    };
  });

  const worldIdx = (seed + 8) % content.worldDetails.length;
  const worldDetails: WorldDetail[] = (content.worldDetails[worldIdx] ?? content.worldDetails[0]).slice();

  const universeName = ctx?.universe?.name ? String(ctx.universe.name) : "The Baseline Continuum";
  const universeCode = ctx?.universe?.code ? String(ctx.universe.code) : "U-BASE-01";
  const universeDescription = `Set in ${universeName} (${universeCode}), a reality whose physical and cosmic parameters shape the daily possibilities and social rhythms of this life.`;

  // Dossier building
  const dilemmaIdx = (seed + 9) % content.dilemmas.length;
  const dilemma = content.dilemmas[dilemmaIdx] ?? "Balancing personal integrity against the demands of institutional survival.";

  const nearFutureIdx = (seed + 10) % content.nearFutures.length;
  const nearFuture = content.nearFutures[nearFutureIdx] ?? "Preparing for the next season of quiet, essential community service.";

  const longArcIdx = (seed + 11) % content.longArcs.length;
  const longArc = content.longArcs[longArcIdx] ?? "Will leave behind an enduring baseline of honest work that benefits future generations.";
  const trajectory = { nearFuture, longArc };

  const skillIdx = (seed + 12) % content.skills.length;
  const skills = (content.skills[skillIdx] ?? content.skills[0]).slice();

  const motIdx = (seed + 13) % content.motivations.length;
  const motivations = (content.motivations[motIdx] ?? content.motivations[0]).slice();

  const relIdx = (seed + 14) % content.relationships.length;
  const relationships = (content.relationships[relIdx] ?? content.relationships[0]).slice();

  const achIdx = (seed + 15) % content.achievements.length;
  const achievements = (content.achievements[achIdx] ?? content.achievements[0]).slice();

  const failIdx = (seed + 16) % content.failures.length;
  const failures = (content.failures[failIdx] ?? content.failures[0]).slice();

  const visitorName = ctx?.name ? String(ctx.name) : "Traveler";
  const ambitionStr = ctx?.resolved?.ambition ? String(ctx.resolved.ambition) : "your ambition";
  const lifePathStr = ctx?.resolved?.lifePath ? String(ctx.resolved.lifePath) : "your life path";

  const background = `Formed through formative apprenticeships and local schooling, ${personalName} developed a lifelong commitment to ${occupation.toLowerCase()}. Raised with an unhurried appreciation for honest craft, they chose a path rooted in practical service rather than distant abstraction.`;

  const personality = `Reflective, quietly resolute, and observant. ${personalName} approaches human friction with measured patience, preferring tangible repairs over ideological dispute.`;

  const influence: string[] = [
    `Shaped for ${visitorName}, this persona reflects your core drive toward ${ambitionStr}.`,
    `Your instinctive inclination toward ${lifePathStr} directed how ${personalName} responds to pivotal turning points.`,
    `The choices recorded here embody how small, deliberate decisions can redefine an entire lifetime.`,
    `Every trait and dilemma was grounded in the overarching physical constants of ${universeName}.`,
  ];

  const dossier: Dossier = {
    background,
    personality,
    skills,
    motivations,
    relationships,
    achievements,
    failures,
    dilemma,
    trajectory,
    influence,
  };

  return {
    name: personalName,
    title,
    tagline,
    occupation,
    traits,
    story: {
      origin,
      turningPoint,
      today,
    },
    choices,
    worldDetails,
    universeDescription,
    dossier,
  };
}
